/**
 * Template error handling utilities
 * Provides comprehensive error handling for template operations
 */

/**
 * Template error types
 */
export const TEMPLATE_ERROR_TYPES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  EDIT_PERMISSION_DENIED: 'EDIT_PERMISSION_DENIED',
  DUPLICATE_TEMPLATE_NAME: 'DUPLICATE_TEMPLATE_NAME',
  WHATSAPP_SYNC_FAILED: 'WHATSAPP_SYNC_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  RATE_LIMIT_ERROR: 'RATE_LIMIT_ERROR',
  FILE_UPLOAD_ERROR: 'FILE_UPLOAD_ERROR',
  TEMPLATE_APPROVAL_ERROR: 'TEMPLATE_APPROVAL_ERROR',
  BULK_OPERATION_ERROR: 'BULK_OPERATION_ERROR',
};

/**
 * Template error class
 */
export class TemplateError extends Error {
  constructor(code, message, details = null, field = null) {
    super(message);
    this.name = 'TemplateError';
    this.code = code;
    this.details = details;
    this.field = field;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Create a template error
 * @param {string} code - Error code
 * @param {string} message - Error message
 * @param {Object} [details] - Additional error details
 * @param {string} [field] - Field name if field-specific error
 * @returns {TemplateError} Template error instance
 */
export const createTemplateError = (
  code,
  message,
  details = null,
  field = null
) => {
  return new TemplateError(code, message, details, field);
};

/**
 * Parse API error response
 * @param {Object} error - API error response
 * @returns {TemplateError} Parsed template error
 */
export const parseApiError = error => {
  if (!error) {
    return createTemplateError(
      TEMPLATE_ERROR_TYPES.NETWORK_ERROR,
      'Network error occurred'
    );
  }

  if (error instanceof TemplateError || error.name === 'TemplateError') {
    return error;
  }

  // Handle different error response formats
  if (error.response) {
    const { status, data } = error.response;

    switch (status) {
      case 400:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.VALIDATION_ERROR,
          data.message || 'Validation error occurred',
          data.details,
          data.field
        );

      case 401:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.AUTHENTICATION_ERROR,
          data?.message || 'Authentication required. Please login again.',
          { status, originalError: data }
        );

      case 403:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.EDIT_PERMISSION_DENIED,
          data?.message || 'You do not have permission to edit this template',
          { status, originalError: data }
        );

      case 404:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.TEMPLATE_NOT_FOUND,
          data?.message || 'Template not found',
          { status, originalError: data }
        );

      case 409:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.DUPLICATE_TEMPLATE_NAME,
          data?.message || 'Template name already exists',
          { status, originalError: data }
        );

      case 429:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.RATE_LIMIT_ERROR,
          data?.message || 'Rate limit exceeded. Please try again later.',
          { status, originalError: data }
        );

      case 500:
      case 502:
      case 503:
      case 504:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.SERVER_ERROR,
          data?.message || 'Server error occurred. Please try again later.',
          { status, originalError: data }
        );

      default:
        return createTemplateError(
          TEMPLATE_ERROR_TYPES.NETWORK_ERROR,
          data.message || `HTTP ${status}: ${error.response.statusText}`,
          { status, originalError: data }
        );
    }
  }

  // Handle network errors
  if (
    error.code === 'NETWORK_ERROR' ||
    error.message?.includes('Network Error')
  ) {
    return createTemplateError(
      TEMPLATE_ERROR_TYPES.NETWORK_ERROR,
      'Network error occurred',
      { originalError: error }
    );
  }

  // Handle timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return createTemplateError(
      TEMPLATE_ERROR_TYPES.NETWORK_ERROR,
      'Request timeout. Please check your connection and try again.',
      { originalError: error }
    );
  }

  // Handle generic errors
  return createTemplateError(
    TEMPLATE_ERROR_TYPES.NETWORK_ERROR,
    error.message && error.message.trim() !== ''
      ? error.message
      : 'Network error occurred',
    { originalError: error }
  );
};

/**
 * Handle template operation errors
 * @param {Function} operation - Async operation to execute
 * @param {Object} [context] - Error context information
 * @returns {Promise} Operation result or throws TemplateError
 */
export const handleTemplateOperation = async (operation, context = {}) => {
  try {
    return await operation();
  } catch (error) {
    const templateError = parseApiError(error);

    // Add context information
    if (context.operation) {
      templateError.details = {
        ...templateError.details,
        operation: context.operation,
        ...context,
      };
    }

    // Log error for debugging
    console.error('Template operation error:', {
      code: templateError.code,
      message: templateError.message,
      details: templateError.details,
      context,
    });

    throw templateError;
  }
};

/**
 * Get user-friendly error message
 * @param {TemplateError} error - Template error
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyErrorMessage = error => {
  if (!error || !error.code) {
    return 'Network error occurred';
  }

  const errorMessages = {
    [TEMPLATE_ERROR_TYPES.VALIDATION_ERROR]:
      'Please check your input and try again.',
    [TEMPLATE_ERROR_TYPES.TEMPLATE_NOT_FOUND]:
      'Template not found. It may have been deleted.',
    [TEMPLATE_ERROR_TYPES.EDIT_PERMISSION_DENIED]:
      'You do not have permission to edit this template.',
    [TEMPLATE_ERROR_TYPES.DUPLICATE_TEMPLATE_NAME]:
      'A template with this name already exists.',
    [TEMPLATE_ERROR_TYPES.WHATSAPP_SYNC_FAILED]:
      'Failed to sync with WhatsApp. Please try again.',
    [TEMPLATE_ERROR_TYPES.NETWORK_ERROR]:
      'Network error. Please check your connection.',
    [TEMPLATE_ERROR_TYPES.SERVER_ERROR]:
      'Server error. Please try again later.',
    [TEMPLATE_ERROR_TYPES.AUTHENTICATION_ERROR]:
      'Please login again to continue.',
    [TEMPLATE_ERROR_TYPES.RATE_LIMIT_ERROR]:
      'Too many requests. Please wait a moment.',
    [TEMPLATE_ERROR_TYPES.FILE_UPLOAD_ERROR]:
      'File upload failed. Please try again.',
    [TEMPLATE_ERROR_TYPES.TEMPLATE_APPROVAL_ERROR]:
      'Template approval failed. Please check the requirements.',
    [TEMPLATE_ERROR_TYPES.BULK_OPERATION_ERROR]:
      'Bulk operation failed. Some items may not have been processed.',
  };

  return errorMessages[error.code] || error.message || 'Network error occurred';
};

/**
 * Check if error is retryable
 * @param {TemplateError} error - Template error
 * @returns {boolean} Whether error is retryable
 */
export const isRetryableError = error => {
  if (!error || !error.code) return false;

  const retryableErrors = [
    TEMPLATE_ERROR_TYPES.NETWORK_ERROR,
    TEMPLATE_ERROR_TYPES.SERVER_ERROR,
    TEMPLATE_ERROR_TYPES.RATE_LIMIT_ERROR,
  ];

  return retryableErrors.includes(error.code);
};

/**
 * Retry operation with exponential backoff
 * @param {Function} operation - Operation to retry
 * @param {Object} [options] - Retry options
 * @param {number} [options.maxRetries] - Maximum number of retries
 * @param {number} [options.baseDelay] - Base delay in milliseconds
 * @param {number} [options.maxDelay] - Maximum delay in milliseconds
 * @returns {Promise} Operation result
 */
export const retryOperation = async (operation, options = {}) => {
  const { maxRetries = 3, baseDelay = 1000, maxDelay = 10000 } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Don't retry if error is not retryable
      if (!isRetryableError(error)) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);

      // Add jitter to prevent thundering herd
      const jitter = Math.random() * 0.1 * delay;
      const totalDelay = delay + jitter;

      await new Promise(resolve => setTimeout(resolve, totalDelay));
    }
  }

  throw lastError;
};

/**
 * Error boundary component for template operations
 */
export class TemplateErrorBoundary {
  constructor() {
    this.errors = [];
    this.maxErrors = 10;
  }

  /**
   * Add error to boundary
   * @param {TemplateError} error - Error to add
   */
  addError(error) {
    this.errors.push({
      ...error,
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
    });

    // Keep only the most recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }
  }

  /**
   * Get recent errors
   * @param {number} [count] - Number of recent errors to return
   * @returns {Array} Recent errors
   */
  getRecentErrors(count = 5) {
    return this.errors.slice(-count);
  }

  /**
   * Clear all errors
   */
  clearErrors() {
    this.errors = [];
  }

  /**
   * Get error summary
   * @returns {Object} Error summary
   */
  getErrorSummary() {
    const summary = {
      total: this.errors.length,
      byType: {},
      recent: this.getRecentErrors(3),
    };

    this.errors.forEach(error => {
      summary.byType[error.code] = (summary.byType[error.code] || 0) + 1;
    });

    return summary;
  }
}

/**
 * Global error boundary instance
 */
export const globalTemplateErrorBoundary = new TemplateErrorBoundary();

/**
 * Error reporting utility
 * @param {TemplateError} error - Error to report
 * @param {Object} [context] - Additional context
 */
export const reportTemplateError = (error, context = {}) => {
  // Add to global error boundary
  globalTemplateErrorBoundary.addError(error);

  // Log error for debugging
  console.error('Template error reported:', {
    code: error.code,
    message: error.message,
    details: error.details,
    field: error.field,
    context,
    timestamp: error.timestamp,
  });

  // In production, you might want to send errors to an error tracking service
  // Example: Sentry.captureException(error);
};

/**
 * Create error handler for specific operations
 * @param {string} operation - Operation name
 * @returns {Function} Error handler function
 */
export const createErrorHandler = operation => {
  return (error, context = {}) => {
    const templateError = parseApiError(error);
    reportTemplateError(templateError, { operation, ...context });
    return templateError;
  };
};

/**
 * Error recovery strategies
 */
export const ERROR_RECOVERY_STRATEGIES = {
  /**
   * Retry with exponential backoff
   */
  RETRY: 'retry',

  /**
   * Fallback to cached data
   */
  FALLBACK_CACHE: 'fallback_cache',

  /**
   * Show user-friendly message
   */
  SHOW_MESSAGE: 'show_message',

  /**
   * Redirect to safe state
   */
  REDIRECT: 'redirect',

  /**
   * Refresh authentication
   */
  REFRESH_AUTH: 'refresh_auth',
};

/**
 * Get recovery strategy for error
 * @param {TemplateError} error - Template error
 * @returns {string} Recovery strategy
 */
export const getRecoveryStrategy = error => {
  if (!error || !error.code) {
    return ERROR_RECOVERY_STRATEGIES.SHOW_MESSAGE;
  }

  const strategyMap = {
    [TEMPLATE_ERROR_TYPES.NETWORK_ERROR]: ERROR_RECOVERY_STRATEGIES.RETRY,
    [TEMPLATE_ERROR_TYPES.SERVER_ERROR]: ERROR_RECOVERY_STRATEGIES.RETRY,
    [TEMPLATE_ERROR_TYPES.RATE_LIMIT_ERROR]: ERROR_RECOVERY_STRATEGIES.RETRY,
    [TEMPLATE_ERROR_TYPES.AUTHENTICATION_ERROR]:
      ERROR_RECOVERY_STRATEGIES.REFRESH_AUTH,
    [TEMPLATE_ERROR_TYPES.TEMPLATE_NOT_FOUND]:
      ERROR_RECOVERY_STRATEGIES.REDIRECT,
    [TEMPLATE_ERROR_TYPES.EDIT_PERMISSION_DENIED]:
      ERROR_RECOVERY_STRATEGIES.SHOW_MESSAGE,
    [TEMPLATE_ERROR_TYPES.VALIDATION_ERROR]:
      ERROR_RECOVERY_STRATEGIES.SHOW_MESSAGE,
    [TEMPLATE_ERROR_TYPES.DUPLICATE_TEMPLATE_NAME]:
      ERROR_RECOVERY_STRATEGIES.SHOW_MESSAGE,
  };

  return strategyMap[error.code] || ERROR_RECOVERY_STRATEGIES.SHOW_MESSAGE;
};

export default {
  TEMPLATE_ERROR_TYPES,
  TemplateError,
  createTemplateError,
  parseApiError,
  handleTemplateOperation,
  getUserFriendlyErrorMessage,
  isRetryableError,
  retryOperation,
  TemplateErrorBoundary,
  globalTemplateErrorBoundary,
  reportTemplateError,
  createErrorHandler,
  ERROR_RECOVERY_STRATEGIES,
  getRecoveryStrategy,
};
